import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Card,
  Row,
  Col,
  Tooltip,
  Pagination,
  Select,
  Table,
  Dropdown,
  MenuProps,
  Space,
  DatePicker,
  Form,
} from "antd";
import {
  InfoCircleOutlined,
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ChannelEnum } from "../constants/enums";
import {
  CompletedCampaign,
  CompletedCampaignViewModel,
  Type,
} from "../models/campaign";
import { ColumnsType } from "antd/es/table";
import { CHANNEL_OPTIONS, handleSelectAll } from "../utils/options";
import Search from "antd/es/input/Search";
import SegmentCell from "../components/common/SegmentCell";
import dayjs, { Dayjs } from "dayjs";
import { campaignService } from "../services/campaignService";
import { useBrandStore } from "../store/brandStore";
import { brandMemberSegmentService } from "../services/brandMemberSegmentService";
import { Segment } from "../models/segment";
import { RoutePaths } from "../routes/route";
import { useLocation, useNavigate } from "react-router-dom";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import moment from "moment";
import isBetween from "dayjs/plugin/isBetween";
import { calculateCampaignStats } from "../utils/rfmHelper";
import { ChannelMap } from "../constants/labels";
import { ChannelKey } from "../constants/type";

dayjs.extend(isBetween);

type ActionKey =
  | "view"
  | "edit"
  | "cancel"
  | "duplicate"
  | "pause"
  | "stop"
  | "resume"
  | "delete";

const channelIcon = (c: ChannelEnum) => {
  switch (c) {
    case ChannelEnum.Email:
      return <i className="icon-mail" />;
    case ChannelEnum.Sms:
      return <i className="icon-sms" />;
    case ChannelEnum.WhatsAppStandard:
      return <i className="icon-whatsapp" />;
    default:
      return <i className="icon-mail" />;
  }
};
const ACTION_ITEMS: MenuProps["items"] = [
  {
    key: "view",
    icon: <i className="icon-view-black" />,
    label: "View Detail",
  },
];

const TargetMarketingAll: React.FC = () => {
  // =====================
  // State Declarations
  // =====================

  // UI & State Management
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>("");
  const handleAction = useHandleAction();
  const [profile, setProfile] = useState<string[]>([]);
  const ALL_VALUE = "All";

  const [segments, setSegments] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);

  // function handleSelectAll(
  //   valueList: string[],
  //   prevValue: string[],
  //   allOptions: { value: string }[],
  //   setter: (v: string[]) => void
  // ) {
  //   const totalReal = allOptions.length;
  //   const isAllSelectedNow = valueList.includes(ALL_VALUE);
  //   const wasAllSelectedBefore = prevValue.includes(ALL_VALUE);

  //   if (isAllSelectedNow && !wasAllSelectedBefore) {
  //     setter([ALL_VALUE, ...allOptions.map((o) => o.value)]);
  //     return;
  //   }

  //   if (
  //     !isAllSelectedNow &&
  //     wasAllSelectedBefore &&
  //     valueList.length === totalReal
  //   ) {
  //     setter([]);
  //     return;
  //   }

  //   if (wasAllSelectedBefore && valueList.length <= totalReal) {
  //     setter(valueList.filter((v) => v !== ALL_VALUE));
  //     return;
  //   }

  //   if (valueList.length === totalReal) {
  //     setter([ALL_VALUE, ...valueList]);
  //     return;
  //   }

  //   setter(valueList);
  // }

  const location = useLocation();
  const [listBrandRFMProfile, setListBrandRFMProfile] = useState<
    { Id: number; Name: string; IsActive: boolean }[]
  >([]);

  const { brandId } = useBrandStore();
  function useHandleAction() {
    return useCallback((key: ActionKey, record: any) => {
      switch (key) {
        case "view":
          navigate(
            `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${record.CampaignId}`,
          );
          break;
      }
    }, []);
  }
  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const endRef = useRef<any>(null);
  const columns: ColumnsType<CompletedCampaignViewModel> = [
    {
      title: "Campaign Name",
      dataIndex: "CampaignName",
      key: "name",
      width: "20%",
      render: (t) => <span>{t}</span>,
    },
    {
      title: "Channel",
      dataIndex: "Channel",
      key: "channel",
      render: (c: string) => {
        const key = c as ChannelKey;

        return (
          <Space>
            {channelIcon(ChannelMap[key])}
            {c}
          </Space>
        );
      },
    },
    { title: "Type", dataIndex: "ScheduleType", key: "type", width: 170 },
    {
      title: "Completed on",
      dataIndex: "CampaignEnd",
      key: "completedOn",
      render: (v?: string) => (v ? v : "-"),
    },
    {
      title: "Segment",
      dataIndex: "Segments",
      key: "segments",
      render: (segs: Segment[]) => {
        const names = Array.from(new Set(segs.map((s) => s.SegmentName)));
        return <SegmentCell segs={names} />;
      },
    },
    {
      title: "Criteria Profile",
      dataIndex: "RFMProfile",
      key: "criteriaProfile",
    },
    {
      title: "CTR",
      dataIndex: "CTR",
      key: "ctr",
      width: "5%",
      render: (v?: number) => <span>{v} %</span>,
    },
    {
      title: "ROI",
      dataIndex: "ROI",
      key: "roi",
      width: "10%",
      render: (v?: number) => <span>{v?.toLocaleString("en-US")} % </span>,
    },
    {
      title: "Rewards",
      dataIndex: "RevenueRewards",
      key: "revenue",
      width: "10%",
      render: (v?: number) => <span>${v?.toLocaleString("en-US")} </span>,
    },
    {
      title: "Campaign",
      dataIndex: "RevenueCampaign",
      key: "revenue",
      width: "10%",
      render: (v?: number) => <span>${v?.toLocaleString("en-US")} </span>,
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 60,
      render: (_: unknown, record: CompletedCampaignViewModel) => (
        <Dropdown
          menu={{
            items: ACTION_ITEMS,
            onClick: ({ key }) => handleAction(key as ActionKey, record),
          }}
          trigger={["click"]}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];
  const [campaigns, setCampaigns] = useState<
    CompletedCampaignViewModel[] | null
  >(null);
  const [filteredCampaigns, setFilteredCampaigns] = useState<
    CompletedCampaignViewModel[]
  >([]);

  const [listBrandMemberSegment, setListBrandMemberSegment] = useState<
    { Id: number; Name: string }[]
  >([]);

  const fetchCampaignData = async (): Promise<
    CompletedCampaignViewModel[] | null
  > => {
    try {
      if (!brandId) return null;

      const response = await campaignService.getTopPerformingCampaigns(
        brandId,
        null,
      );

      return response as CompletedCampaignViewModel[];
    } catch (error) {
      console.error("Error fetching history campaign:", error);
      return null;
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        if (data) {
          setListBrandRFMProfile(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchBrandMemberSegment = async () => {
    if (brandId) {
      try {
        const data =
          await brandMemberSegmentService.listBrandMemberSegment(brandId);
        if (data) {
          setListBrandMemberSegment(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const filtered = useMemo(() => {
    return filteredCampaigns?.filter((r) => {
      const textOk =
        !search || r.CampaignName.toLowerCase().includes(search.toLowerCase());
      // || r.criteriaProfile.toLowerCase().includes(search.toLowerCase());

      return textOk;
    });
  }, [search, filteredCampaigns, channels, segments, start, end]);

  const paginatedData = filtered?.slice(
    (current - 1) * pageSize,
    current * pageSize,
  );

  const isMobileOrTablet = window.innerWidth <= 1024;
  interface CardData {
    key: string;
    title: string;
    value: string;
    icon: string;
    tooltip: React.ReactNode;
  }
  const initialCardData: CardData[] = [
    {
      key: "card-data-1",
      title: "Revenue via Rewards",
      value: "$" + (0).toLocaleString("en-US"),
      icon: "/icon/icon-sack-dollar.svg",
      tooltip: (
        <>
          Total revenue generated from customers who received and
          <br />
          responded to this campaign (generated with with voucher or point
          usage)
        </>
      ),
    },
    {
      key: "card-data-2",
      title: "Revenue from Campaign",
      value: "$" + (0).toLocaleString("en-US"),
      icon: "/icon/icon-revenue-campaign.svg",
      tooltip: (
        <>
          Total revenue generated from campaign activities based on targeted
          members spending
        </>
      ),
    },
    {
      key: "card-data-3",
      title: "Return of Investment (ROI)",
      value: (0).toLocaleString("en-US") + "%",
      icon: "/icon/icon-roi.svg",
      tooltip: (
        <>
          Return on Investment = (Revenue – Cost) / Cost.
          <br />
          Based on the total campaign cost and revenue.
        </>
      ),
    },
    {
      key: "card-data-4",
      title: "Click Through Rate (CTR)",
      value: (0).toLocaleString("en-US") + "%",
      icon: "/icon/icon-ctr.svg",
      tooltip: (
        <>
          Click-through rate = (Number of clicks / Messages delivered)
          <br />× 100%
        </>
      ),
    },
  ];
  const [cardData, setCardData] = useState<CardData[]>(initialCardData);

  const disabledStartDate = (current: Dayjs) => {
    if (!end) return false;
    return current.isAfter(end, "day");
  };

  const disabledEndDate = (current: Dayjs) => {
    if (!start) return false;
    return current.isBefore(start, "day");
  };

  useEffect(() => {
    if (brandId) {
      fetchCampaignData().then((data: any) => {
        setCampaigns(data);
        setFilteredCampaigns(data);
        const stats = calculateCampaignStats(data);

        setCardData((prev) =>
          prev.map((c) => {
            switch (c.key) {
              case "card-data-1":
                return {
                  ...c,
                  value:
                    "$" +
                    stats.totalRevenueRewards.toLocaleString("en-US", {
                      maximumFractionDigits: 2,
                    }),
                  order: stats.totalOrderRewards,
                };

              case "card-data-2":
                return {
                  ...c,
                  value:
                    "$" +
                    stats.totalRevenueCampaign.toLocaleString("en-US", {
                      maximumFractionDigits: 2,
                    }),
                  order: stats.totalOrderCampaign,
                };

              case "card-data-3":
                return {
                  ...c,
                  value:
                    stats.avgROI.toLocaleString("en-US", {
                      maximumFractionDigits: 2,
                    }) + "%",
                };

              case "card-data-4":
                return {
                  ...c,
                  value:
                    stats.avgCTR.toLocaleString("en-US", {
                      maximumFractionDigits: 2,
                    }) + "%",
                };

              default:
                return c;
            }
          }),
        );

        // const totalRevenueRewards = data.reduce(
        //   (sum: any, x: any) => sum + (x.RevenueRewards || 0),
        //   0
        // );
        // const totalOrderRewards = data.reduce(
        //   (sum: any, x: any) => sum + (x.TotalOrderRewards || 0),
        //   0
        // );

        // const totalRevenueCampaign = data.reduce(
        //   (sum: any, x: any) => sum + (x.RevenueCampaign || 0),
        //   0
        // );
        // const totalOrderCampaign = data.reduce(
        //   (sum: any, x: any) => sum + (x.TotalOrderCampaign || 0),
        //   0
        // );
        // const avgROI =
        //   data.length > 0
        //     ? data.reduce((sum: any, x: any) => sum + (x.ROI || 0), 0) /
        //       data.length
        //     : 0;

        // const avgCTR =
        //   data.length > 0
        //     ? data.reduce((sum: any, x: any) => sum + (x.CTR || 0), 0) /
        //       data.length
        //     : 0;

        // setCardData((prev) =>
        //   prev.map((c) => {
        //     if (c.key === "card-data-1")
        //       return {
        //         ...c,
        //         value:
        //           "$" +
        //           totalRevenueRewards.toLocaleString("en-US", {
        //             maximumFractionDigits: 2,
        //           }),
        //         order: totalOrderRewards,
        //       };
        //     if (c.key === "card-data-2")
        //       return {
        //         ...c,
        //         value:
        //           "$" +
        //           totalRevenueCampaign.toLocaleString("en-US", {
        //             maximumFractionDigits: 2,
        //           }),
        //         order: totalOrderCampaign,
        //       };
        //     if (c.key === "card-data-3")
        //       return {
        //         ...c,
        //         value:
        //           avgROI.toLocaleString("en-US", {
        //             maximumFractionDigits: 2,
        //           }) + "%",
        //       };
        //     if (c.key === "card-data-4")
        //       return {
        //         ...c,
        //         value:
        //           avgCTR.toLocaleString("en-US", {
        //             maximumFractionDigits: 2,
        //           }) + "%",
        //       };
        //     return c;
        //   })
        // );
      });
      fetchBrandRFMProfile();
      fetchBrandMemberSegment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const applyFilter = () => {
    if (!campaigns) {
      setFilteredCampaigns([]);
      return;
    }
    setCurrent(1);

    let filtered = [...campaigns];

    if (start && end) {
      const startDate = start.startOf("day");
      const endDate = end.endOf("day");

      filtered = filtered.filter((c) => {
        if (!c.CampaignEnd || c.CampaignEnd === "-") return false;

        const campaignDate = dayjs(c.CampaignEnd);

        if (!campaignDate.isValid()) return false;

        const inRange =
          !campaignDate.isBefore(startDate, "day") &&
          !campaignDate.isAfter(endDate, "day");

        return inRange;
      });
    }

    if (channels.length > 0) {
      filtered = filtered.filter((c) => channels.includes(c.Channel));
    }

    if (segments.length > 0) {
      filtered = filtered.filter((c) =>
        c.Segments?.some((s) => String(segments).includes(String(s.SegmentId))),
      );
    }

    if (profile.length > 0) {
      filtered = filtered.filter((c) => profile.includes(c.RFMProfile));
    }

    if (search && search.trim() !== "") {
      const s = search.toLowerCase();
      filtered = filtered.filter((r) =>
        r.CampaignName.toLowerCase().includes(s),
      );
    }
    setFilteredCampaigns(filtered);
    const stats = calculateCampaignStats(filtered);

    setCardData((prev) =>
      prev.map((c) => {
        switch (c.key) {
          case "card-data-1":
            return {
              ...c,
              value:
                "$" +
                stats.totalRevenueRewards.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                }),
              order: stats.totalOrderRewards,
            };

          case "card-data-2":
            return {
              ...c,
              value:
                "$" +
                stats.totalRevenueCampaign.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                }),
              order: stats.totalOrderCampaign,
            };

          case "card-data-3":
            return {
              ...c,
              value:
                stats.avgROI.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                }) + "%",
            };

          case "card-data-4":
            return {
              ...c,
              value:
                stats.avgCTR.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                }) + "%",
            };

          default:
            return c;
        }
      }),
    );
  };

  useEffect(() => {
    applyFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const channelOptionsWithAll = [
    { label: "All Channel", value: ALL_VALUE },
    ...CHANNEL_OPTIONS,
  ];
  const realSegmentOptions = listBrandMemberSegment.map((s) => ({
    label: s.Name,
    value: String(s.Id),
  }));

  const realProfileOptions = useMemo(
    () =>
      listBrandRFMProfile.map((s) => ({
        value: String(s.Name),
      })),
    [listBrandRFMProfile],
  );

  const segmentOptionsWithAll = [
    { label: "All Segment", value: ALL_VALUE },
    ...realSegmentOptions,
  ];

  const profileOptionsWithAll = [
    { label: "All Profile", value: ALL_VALUE },
    ...listBrandRFMProfile.map((s) => ({
      label: s.Name,
      value: String(s.Name),
    })),
  ];

  useEffect(() => {
    setChannels([...channelOptionsWithAll.map((o) => o.value)]);
    setSegments([...segmentOptionsWithAll.map((o) => o.value)]);
    setProfile([...profileOptionsWithAll.map((o) => o.value)]);
  }, [listBrandMemberSegment, listBrandRFMProfile]);

  return (
    <>
      <div>
        <Card
          loading={loading}
          title={
            <Row justify="space-between" align="middle">
              <Col xs={12} md={12} xl={12}>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>Campaign Filter</span>
                </span>
              </Col>
              <Col xs={12} md={12} xl={12} style={{ textAlign: "end" }}>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => window.history.back()}
                >
                  Back
                </Button>
              </Col>
            </Row>
          }
        >
          <Form layout="vertical">
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={24} md={8} lg={8}>
                <Form.Item label="From Date" className="label-filter">
                  <DatePicker
                    style={{ width: "100%", height: "unset !important" }}
                    value={start}
                    format="YYYY-MM-DD"
                    placeholder="YYYY - MM - DD"
                    disabledDate={disabledStartDate}
                    onChange={(val) => {
                      setStart(val);
                      setTimeout(() => endRef.current?.focus(), 0);
                    }}
                  />
                  {/* <DatePicker
                    style={{ width: "100%", height: "unset !important" }}
                  /> */}
                </Form.Item>
              </Col>
              <Col xl={8} md={8} xs={12}>
                <Form.Item label="To Date" className="label-filter">
                  <DatePicker
                    style={{ width: "100%", height: "unset !important" }}
                    value={end}
                    format="YYYY-MM-DD"
                    placeholder="YYYY - MM - DD"
                    disabledDate={disabledEndDate}
                    onChange={(val) => setEnd(val)}
                    ref={endRef}
                  />
                </Form.Item>
              </Col>
              <Col xl={8} md={8} xs={12}>
                <Form.Item label="Channel" className="label-filter">
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    value={channels}
                    onChange={(v) =>
                      handleSelectAll(v, channels, CHANNEL_OPTIONS, setChannels)
                    }
                    maxTagCount={0}
                    maxTagPlaceholder={() =>
                      channels.includes(ALL_VALUE)
                        ? "All Channel"
                        : `${channels.length}/${CHANNEL_OPTIONS.length} selected`
                    }
                    placeholder="All Channel"
                    options={channelOptionsWithAll}
                    showSearch={false}
                    filterOption={false}
                  />
                </Form.Item>
              </Col>
              <Col xl={8} md={8} xs={12}>
                <Form.Item label="Segment" className="label-filter">
                  <Select<string[]>
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    value={segments}
                    onChange={(v) =>
                      handleSelectAll(
                        v,
                        segments,
                        realSegmentOptions,
                        setSegments,
                      )
                    }
                    maxTagCount={0}
                    maxTagPlaceholder={() =>
                      segments.includes(ALL_VALUE)
                        ? "All Segment"
                        : `${segments.length}/${realSegmentOptions.length} selected`
                    }
                    placeholder="All Segment"
                    options={segmentOptionsWithAll}
                    showSearch={false}
                    filterOption={false}
                  />
                </Form.Item>
              </Col>
              <Col xl={8} md={8} xs={12}>
                <Form.Item label="Criteria Profile" className="label-filter">
                  <Select<string[]>
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    value={profile}
                    onChange={(v) =>
                      handleSelectAll(
                        v,
                        profile,
                        realProfileOptions,
                        setProfile,
                      )
                    }
                    placeholder="All Criteria Profile"
                    options={profileOptionsWithAll}
                    maxTagCount={0}
                    maxTagPlaceholder={() =>
                      profile.includes(ALL_VALUE)
                        ? "All Profile"
                        : `${profile.length}/${realProfileOptions.length} selected`
                    }
                    showSearch={false}
                    filterOption={false}
                  />
                </Form.Item>
              </Col>
              <Col xl={8} md={8} xs={24}>
                <Form.Item label=" ">
                  <Button
                    type="primary"
                    style={{ width: "100%" }}
                    onClick={applyFilter}
                  >
                    <SearchOutlined />
                    Apply Filter
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        <Card
          loading={loading}
          title={
            <Row justify="space-between" align="middle" gutter={[12, 12]}>
              <Col xs={24} md={12} xl={12}>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>
                    Campaign Performance Summary
                  </span>
                </span>
              </Col>
              <Col
                xs={24}
                md={12}
                xl={12}
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  type="primary"
                  style={{
                    backgroundColor: "#E9F4FB",
                    color: "#1565C0",
                    width: isMobileOrTablet ? "100%" : "50%",
                  }}
                >
                  Last Update:{" "}
                  <strong>
                    {moment().subtract(1, "days").format("YYYY-MM-DD")} 23:59:59
                  </strong>
                </Button>
              </Col>
            </Row>
          }
          style={{ marginTop: 10 }}
        >
          <Row gutter={[12, 12]}>
            {cardData.map((item, index) => {
              return (
                <Col md={12} xl={6} key={index} className="w-100">
                  <Card className="card-member">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: 70,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 16,
                            lineHeight: "1.2",
                          }}
                        >
                          {item.title}
                          {item.tooltip && (
                            <Tooltip title={item.tooltip}>
                              <InfoCircleOutlined
                                style={{
                                  fontSize: 14,
                                  marginLeft: 6,
                                  color: "#888",
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>
                          {item.value}
                        </div>
                      </div>
                      <img src={item.icon} alt="icon" width="50" height="50" />
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
        <Card
          loading={loading}
          title={
            <Row justify="space-between" align="middle" gutter={[12, 12]}>
              <Col xs={24} md={12} xl={12}>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>
                    Completed Campaign History
                  </span>
                </span>
              </Col>
              <Col xs={24} md={12} xl={12}>
                <Search
                  placeholder="Search Campaign Name"
                  allowClear
                  prefix={<SearchOutlined />}
                  enterButton="Search"
                  size="large"
                  onSearch={(value) => {
                    setSearch(value);
                  }}
                  style={{
                    width: isMobileOrTablet ? "100%" : "50%",
                    float: "right",
                  }}
                />
              </Col>
            </Row>
          }
          style={{ marginTop: 10 }}
        >
          {" "}
          <Table
            rowKey="key"
            columns={columns}
            dataSource={paginatedData}
            bordered={false}
            size="middle"
            pagination={false}
            scroll={{ x: "max-content" }}
          />
          <Row
            justify={isMobileOrTablet ? "center" : "space-between"}
            style={{
              marginTop: 16,
              textAlign: isMobileOrTablet ? "center" : "unset",
            }}
          >
            <Col
              xs={24}
              xl={12}
              md={12}
              style={{ marginBottom: isMobileOrTablet ? 10 : "unset" }}
            >
              Results per Page{" "}
              <Select
                value={pageSize}
                style={{ width: 80 }}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrent(1);
                }}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
              />{" "}
              of {filtered?.length} items
            </Col>

            <Col xs={24} xl={12} md={12}>
              <Pagination
                style={{
                  display: "inline-flex",
                  float: isMobileOrTablet ? undefined : "inline-end",
                }}
                current={current}
                pageSize={pageSize}
                total={filtered?.length}
                showSizeChanger={false}
                onChange={(page) => setCurrent(page)}
              />
            </Col>
          </Row>
        </Card>
      </div>
    </>
  );
};

export default TargetMarketingAll;
